import React, { useMemo, useState } from 'react';
import { downloadPostGuardCsv } from './perfHubPostGuardExport';
import PerfHubSectionInfo from './PerfHubSectionInfo';
import { POST_GUARD_INFO_LEAD, POST_GUARD_STATUS_HELP } from './perfHubPostGuardHelp';

export const POST_GUARD_REVIEW_VERIFY =
  'Review suggested: independently re-verify the live post, caption, and deliverables before releasing any payment to this creator.';

export const POST_GUARD_NOT_LIVE_NOTICE =
  'Post not live: the synced post link is missing or invalid. Verify the post URL, then refresh Instagram data.';

export const POST_GUARD_AWAITING_SYNC_NOTICE =
  'Awaiting sync: we do not have Instagram data for this post yet. Use Refresh Instagram data — we pull caption, hashtags, media type, and metrics via the public link.';

// Legacy alias retained for any external import.
export const POST_GUARD_NOT_CONNECTED_NOTICE = POST_GUARD_AWAITING_SYNC_NOTICE;

const PG_FILTERS_FALLBACK = [
  { id: 'all', label: 'All' },
  { id: 'good', label: 'Looks good' },
  { id: 'review', label: 'Review suggested' },
  { id: 'not_live', label: 'Post not live' },
  { id: 'awaiting_sync', label: 'Awaiting sync' },
];

const READINESS_LABELS = {
  good: 'Looks good',
  review: 'Review suggested',
  not_live: 'Post not live',
  awaiting_sync: 'Awaiting sync',
  // Legacy alias — older backend builds returned not_connected.
  not_connected: 'Awaiting sync',
};

function normalizeReadiness(readiness) {
  if (readiness === 'good' || readiness === 'green') return 'good';
  if (readiness === 'not_live') return 'not_live';
  if (readiness === 'awaiting_sync' || readiness === 'not_connected') return 'awaiting_sync';
  return 'review';
}

function ReadinessLight({ readiness }) {
  const bucket = normalizeReadiness(readiness);
  const label = READINESS_LABELS[bucket] || readiness;
  return (
    <span className={`ph-pg-light ph-pg-light--${bucket}`} title={label}>
      <span className="ph-pg-light-dot" aria-hidden />
      {label}
    </span>
  );
}

function CheckList({ checks }) {
  if (!checks?.length) return null;
  return (
    <ul className="ph-pg-checks">
      {checks.map((c) => (
        <li key={c.id} className={`ph-pg-check ph-pg-check--${c.status}`}>
          <span className="ph-pg-check-label">{c.label}</span>
          <span className="ph-pg-check-msg">{c.message}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Post Guard — informational status per post (no payout actions in product).
 */
export default function PerfHubPostGuardPanel({
  postGuard,
  onEditRules,
  onRefreshInstagram,
  refreshBusy = false,
  canRefreshInstagram = false,
  campaignName,
  exportEnabled = true,
}) {
  const [expandedId, setExpandedId] = useState(null);
  const [filterId, setFilterId] = useState('all');

  const summary = postGuard?.summary || {};
  const posts = postGuard?.posts || [];
  const configured = postGuard?.brief_configured;
  const hasRules = postGuard?.brief_has_rules;
  const filters = postGuard?.filters?.length ? postGuard.filters : PG_FILTERS_FALLBACK;

  const goodCount = summary.good ?? summary.green ?? 0;
  const reviewCount = summary.review ?? 0;
  const notLiveCount = summary.not_live ?? 0;
  // Prefer the new field; fall back to legacy not_connected count.
  const awaitingSyncCount = summary.awaiting_sync ?? summary.not_connected ?? 0;

  const filteredPosts = useMemo(() => {
    if (filterId === 'all') return posts;
    return posts.filter((p) => normalizeReadiness(p.readiness) === filterId);
  }, [posts, filterId]);

  const canDownload = exportEnabled && configured && filteredPosts.length > 0;

  const handleDownload = () => {
    if (!canDownload) return;
    downloadPostGuardCsv(filteredPosts, {
      campaignName,
      filterId,
      filters,
    });
  };

  return (
    <section className="ph-pg-panel" aria-labelledby="ph-pg-heading">
      <div className="ph-pg-head">
        <div>
          <h4 id="ph-pg-heading" className="ph-pg-title">
            <span className="ph-pg-title-icon" aria-hidden>
              🛡️
            </span>
            Post Guard
            <PerfHubSectionInfo label="Post Guard" className="ph-section-info--rich">
              <p className="ph-section-info-tip-lead">{POST_GUARD_INFO_LEAD}</p>
              <ul className="ph-section-info-status-list">
                {POST_GUARD_STATUS_HELP.map((s) => (
                  <li key={s.id}>
                    <strong>{s.label}</strong>
                    <span> — {s.description}</span>
                  </li>
                ))}
              </ul>
            </PerfHubSectionInfo>
          </h4>
        </div>
        <div className="ph-pg-head-actions">
          {canRefreshInstagram && onRefreshInstagram && (
            <button
              type="button"
              className="btn-outline ph-pg-edit-btn"
              disabled={refreshBusy}
              onClick={onRefreshInstagram}
              title="Fetch latest Instagram captions and metrics for all connected creators"
            >
              {refreshBusy ? 'Refreshing…' : 'Refresh Instagram data'}
            </button>
          )}
          {configured && (
            <button
              type="button"
              className="btn-outline ph-pg-edit-btn"
              disabled={!canDownload}
              onClick={handleDownload}
              title={
                !exportEnabled
                  ? 'CSV export requires a paid plan'
                  : filteredPosts.length === 0
                    ? 'No rows match this filter'
                    : 'Download Post Guard data for the current filter'
              }
            >
              Download CSV {!exportEnabled ? '(paid)' : ''}
            </button>
          )}
          {onEditRules && (
            <button type="button" className="btn-outline ph-pg-edit-btn" onClick={onEditRules}>
              {hasRules ? 'Edit rules' : 'Set up rules'}
            </button>
          )}
        </div>
      </div>

      {!configured && (
        <div className="ph-pg-empty">
          <p>Add creators to this campaign to see Post Guard status for each post.</p>
        </div>
      )}

      {configured && (
        <>
          {!hasRules && (
            <p className="ph-pg-filter-summary ph-cell-muted">
              No optional rules set — we only check that each post is synced and live. Use Edit
              rules to add hashtags, handles, format, or CTA checks.
            </p>
          )}

          <div className="ph-pg-summary-chips">
            <span className="ph-pg-chip ph-pg-chip--good">{goodCount} looks good</span>
            <span className="ph-pg-chip ph-pg-chip--review">{reviewCount} review suggested</span>
            <span className="ph-pg-chip ph-pg-chip--not-live">{notLiveCount} post not live</span>
            <span className="ph-pg-chip ph-pg-chip--not-connected">
              {awaitingSyncCount} awaiting sync
            </span>
          </div>

          <div className="ph-pg-filters" role="tablist" aria-label="Filter by status">
            {filters.map((f) => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={filterId === f.id}
                className={`ph-pg-filter ${filterId === f.id ? 'active' : ''}`}
                onClick={() => setFilterId(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {filterId !== 'all' && (
            <p className="ph-pg-filter-summary ph-cell-muted">
              Showing {filteredPosts.length} of {posts.length}
            </p>
          )}

          {filteredPosts.length > 0 ? (
            <div className="ph-table-wrap ph-pg-table-wrap">
              <table className="funds-table enhanced ph-pg-table">
                <thead>
                  <tr>
                    <th>Creator</th>
                    <th>Status</th>
                    <th className="ph-pg-th-actions">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPosts.map((p) => {
                    const rid = p.row_id;
                    const open = expandedId === rid;
                    const bucket = normalizeReadiness(p.readiness);
                    const needsVerify = bucket === 'review';
                    const notLive = bucket === 'not_live';
                    const awaitingSync = bucket === 'awaiting_sync';
                    return (
                      <React.Fragment key={rid}>
                        <tr
                          className={
                            awaitingSync
                              ? 'ph-pg-row--not-connected'
                              : notLive
                                ? 'ph-pg-row--not-live'
                                : needsVerify
                                  ? 'ph-pg-row--review'
                                  : ''
                          }
                        >
                          <td>
                            <strong>@{p.instagram_handle}</strong>
                          </td>
                          <td>
                            <ReadinessLight readiness={p.readiness} />
                          </td>
                          <td className="ph-pg-td-actions">
                            <button
                              type="button"
                              className="btn-outline ph-pg-btn-sm ph-pg-btn-ghost"
                              onClick={() => setExpandedId(open ? null : rid)}
                            >
                              {open ? 'Hide checks' : 'View checks'}
                            </button>
                          </td>
                        </tr>
                        {open && (
                          <tr className="ph-pg-detail-row">
                            <td colSpan={3}>
                              {awaitingSync && (
                                <p className="ph-pg-not-connected-verify" role="alert">
                                  {postGuard?.awaiting_sync_notice ||
                                    postGuard?.not_connected_notice ||
                                    POST_GUARD_AWAITING_SYNC_NOTICE}
                                </p>
                              )}
                              {needsVerify && (
                                <p className="ph-pg-hold-verify" role="alert">
                                  {postGuard?.review_verify_notice || POST_GUARD_REVIEW_VERIFY}
                                </p>
                              )}
                              {notLive && (
                                <p className="ph-pg-not-live-verify" role="alert">
                                  {postGuard?.not_live_notice || POST_GUARD_NOT_LIVE_NOTICE}
                                </p>
                              )}
                              <CheckList checks={p.checks} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="ph-pg-empty-filter ph-cell-muted">No creators match this filter.</p>
          )}
        </>
      )}
    </section>
  );
}
